import { Popover } from "@mui/material";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useModalStore } from "../../store/useModalStore";
import { useUserDetail } from "../../lib/apis/hooks/user.hook";
import { deleteUser, updateUser } from "../../lib/apis/v1/user.service";
import { getQueryClient } from "../../config/queryClient.config";
import FailSnackbar from "../../components/ui/snackbar/fail.component";
import SuccessSnackbar from "../../components/ui/snackbar/success.component";
import type { updateUserForm } from "../../lib/apis/dtos/request/user.request";
import Button from "../../components/ui/button";
import { formatDateTime } from "../../utils/date.utils";
import { usePopover } from "../../store/usePopoverStore";
import ConfirmPopover from "../../components/ui/popover/confirm.component";
import { MODAL_KEY } from "../../lib/constants/modal.constants";
import ExplainTitle from "../../components/layout/header/explain-title";
import { useLoginStore } from "../../store/useLoginStore";
import { ROLES, type Role } from "../../types/auth.types";

export default function AccountDetailPage() {
  // ROUTER
  const navigate = useNavigate();
  const { id } = useParams()
  const parsedId =
    typeof id === "string" ? id : Array.isArray(id) ? id[0] : "0";

  // 상태 관리
  const { email } = useLoginStore((s) => s.access);
  const { openModal } = useModalStore();
  const pushPopover = usePopover((state) => state.pushPopover);
  const popPopover = usePopover((state) => state.popPopover);

  const { data, isSuccess } = useQuery({
    ...useUserDetail(parsedId),
  });
  const { result } = data || {};
  const delMutation = useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: (res) => {
      getQueryClient().invalidateQueries({ queryKey: ["user-list"] });
      const { error } = res;

      if (error) {
        FailSnackbar("계정 삭제 실패");
      } else {
        SuccessSnackbar("계정 삭제 성공");
      }
      navigate("/account");
    },
  });

  const patchMutation = useMutation({
    mutationFn: (form: updateUserForm) => updateUser(form)
  });

  const resetPopoverClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    return pushPopover(
      Popover,
      event.currentTarget,
      {
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
        transformOrigin: { vertical: "top", horizontal: "center" },
        PaperProps: {
          sx: { mt: 3 },
        },
      },
      (id) => (
        <ConfirmPopover
          id={id}
          rightProps={{
            label: "비밀번호 초기화",
            variant: "primary",
            onClick: async () => {
              popPopover(id);
              patchMutation.mutate({
                ...result,
                password: "0000",
              } as updateUserForm, {
                onSuccess: () => {
                  getQueryClient().invalidateQueries({ queryKey: ["user-detail"] });
                  openModal(MODAL_KEY.Alert, {
                    title: "비밀번호 초기화",
                    content: "비밀번호가 '0000'으로 초기화되었습니다."
                  });
                }
              });
            },
          }}
        >
          <p>
            비밀번호를 초기화시키시겠습니까?
            <br />
            비밀번호가 '0000'으로 초기화됩니다.
          </p>
        </ConfirmPopover>
      )
    );
  };

  const deletePopoverClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    return pushPopover(
      Popover,
      event.currentTarget,
      {
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
        transformOrigin: { vertical: "top", horizontal: "center" },
        PaperProps: {
          sx: { mt: 3 },
        },
      },
      (id) => (
        <ConfirmPopover
          id={id}
          rightProps={{
            label: "계정 삭제",
            variant: "danger",
            onClick: async () => {
              popPopover(id);
              delMutation.mutate(parsedId)
            },
          }}
        >
          <p>
            직원 계정을 삭제 하시겠습니까? 현
            <br />
            재 배정된 지점의 담당 권한도 함께
            <br /> 삭제됩니다.
          </p>
        </ConfirmPopover>
      )
    );
  };

  return (
    <div className="p-6">
      <ExplainTitle title="계정 관리" explanation="계정 정보를 관리하세요.">
        <div className="flex-row">
          <Button
            label="비밀번호 초기화"
            variant="outline"
            className="mr-4"
            onClick={resetPopoverClick}
          />
          {email != result?.email && (
            <Button
              onClick={deletePopoverClick}
              variant="outline-red"
              label="계정 삭제"
            />
          )}
        </div>
      </ExplainTitle>
      <table className="w-full">
        <tbody>
          <tr className="content-table-tr">
            <td className="content-table-th">아이디</td>
            <td className="content-table-td">
              {result?.loginId || "-"}
            </td>
            <td className="content-table-th">이메일</td>
            <td className="content-table-td flex-row">
              {result?.email ?? "-"}
              <img
                onClick={() =>
                  openModal(MODAL_KEY.TextField, {
                    title: "이메일 수정",
                    value: result?.email,
                    pattern: "/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\\.[a-zA-Z0-9-.]+$/",
                    helper: "이메일 형식을 맞춰주세요. (ex. abcd@abcd.com)",
                    click: (change: string) => {
                      patchMutation.mutate({
                        ...result,
                        email: change,
                      } as updateUserForm, {
                        onSuccess: (res) => {
                          const { error } = res;
                          if (error) {
                            FailSnackbar("이메일 변경 실패")
                          } else {
                            getQueryClient().invalidateQueries({ queryKey: ["user-detail"] });
                            SuccessSnackbar("이메일 수정 성공")
                          }
                        }
                      });
                    }
                  })
                }
                src="/static/icons/icon_pencil_blue.svg"
                className="w-[24px] ml-[8px] cursor-pointer"
              />

            </td>
          </tr>
          <tr className="content-table-tr">
            <td className="content-table-th">가입일</td>
            <td className="content-table-td">
              {formatDateTime(result?.createdDate || "") || "-"}
            </td>
            <td className="content-table-th">권한</td>
            <td className="content-table-td">{ROLES[result?.role as Role] || "-"}</td>
          </tr>
          <tr className="content-table-tr">
            <td className="content-table-th">이름(닉네임)</td>
            <td className="content-table-td">{result?.username}</td>
            <td className="content-table-th">연락처</td>
            <td className="content-table-td flex flex-row justify-start items-center">
              <span>{result?.phone || "-"}</span>
            </td>
          </tr>
        </tbody>
      </table>

      {/* 담당 지점 정보 */}
      {isSuccess &&
        (result?.stores || []).map((
          {
            id,
            storeName,
            storeAddress,
            storeLink,
          }: {
            id: number;
            storeName: string;
            storeAddress: string;
            storeLink: string;
          },
          index: number
        ) => {
          return (
            <div
              key={index}
              className="border border-[rgba(255,255,255, 0.12)] p-[24px] w-full mt-[24px] rounded-[8px]"
            >
              <div className="flex-row-between">
                <h2 className="p-[16px] pt-0 text-[22px] ">
                  {`${result?.role == "PARTNER" ? "담당" : "보유"} 지점 정보`}
                  <span
                    className="ml-[13px] text-[13px] text-[#1976D2] cursor-pointer"
                    onClick={() => navigate(`/branch/${id}`)}
                  >
                    바로가기 {">"}
                  </span>
                </h2>
              </div>
              <table className="w-full">
                <tbody>
                  <tr className="content-table-tr">
                    <td className="content-table-th">지점명</td>
                    <td className="content-table-td">{storeName || "-"}</td>
                    <td className="content-table-th">지점 주소</td>
                    <td className="content-table-td">
                      {storeAddress || "-"}
                    </td>
                  </tr>
                  <tr className="content-table-tr">
                    <td className="content-table-th">연결 링크</td>
                    <td className="content-table-td" colSpan={2}>
                      {storeLink || "-"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        }
        )}

      <Button
        className="mt-[24px]"
        variant="outline"
        label="담당 지점 배정"
        onClick={() => openModal(MODAL_KEY.AssignBranch, { userId: parsedId, ownerId: result?.ownerId || "" })}
      />
    </div>
  );
}
